import { Cog6ToothIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useRef, useState } from "react";

import {
  Button,
  Divider,
  Headline,
  Paragraph,
  PopoverPanel,
  Small,
  TextArea,
} from "@fronterahealth/frontera-ui-components";

import {
  GetReportsQuery,
  useUpdateOrganizationTemplateLogoUrlMutation,
  useUpdateReportPageHeadingMutation,
} from "@api/graphql/types-and-hooks";
import UploadLogo from "@components/ReportHeaderSection/UploadLogo";
import {
  useGetS3BucketLogoUrlRetriever,
  useGetS3BucketUploadLogoCallback,
} from "@components/ReportHeaderSection/hooks";
import { notifyError } from "@components/notifications/notifications";

type Report = NonNullable<NonNullable<GetReportsQuery["getReports"]>["edges"][0]>["node"];

interface ReportSettingsProps {
  reportIsCompleted: boolean;
  report: Report | null | undefined;
  refetchData: () => Promise<void> | unknown;
}

export const ReportSettings: React.FC<ReportSettingsProps> = ({ reportIsCompleted, report, refetchData }) => {
  const S3BucketUrlRetriever = useGetS3BucketLogoUrlRetriever({ reportId: report?.id ?? "" });
  const S3BucketUploadCallback = useGetS3BucketUploadLogoCallback({ reportId: report?.id ?? "", refetch: refetchData });

  const updateContent = useUpdateReportPageHeadingMutation();
  const updateOrganizationTemplateLogoMutation = useUpdateOrganizationTemplateLogoUrlMutation();

  const [headerContent, setHeaderContent] = useState<string>(report?.headerPageHeading ?? "");

  const ref = useRef<HTMLDivElement>(null);
  const handleOnSave = async (content: string) => {
    await updateContent
      .mutateAsync({
        input: { reportId: report?.id ?? "<missing-report-id>", pageHeadingText: content },
      })
      .then((data) => {
        if (data.updateReportPageHeading?.status) {
          refetchData();
        }
      })
      .catch((error) => {
        console.error("Error while updating report heading : ", error);
        notifyError("Error Updating Report Heading");
      });
  };

  const onSave = async () => {
    await updateOrganizationTemplateLogoMutation
      .mutateAsync({
        input: { reportId: report?.id ?? "<missing-report-id>" },
      })
      .then((data) => {
        if (data.updateOrganizationTemplateLogoUrl?.status) {
          refetchData();
        }
      })
      .catch((error) => {
        console.error("Error removing the header logo : ", error);
        notifyError("Error removing the header logo");
      });
  };

  const popoverContent = (
    <div className="flex flex-col justify-center p-2">
      <div className="w-100 flex justify-between pb-4">
        <Headline size="medium">Report Settings</Headline>
        <XMarkIcon
          onClick={() => {
            setHeaderContent(report?.headerPageHeading ?? "");
            ref?.current?.click();
          }}
          className="h-6 w-6 cursor-pointer bg-transparent text-text-primary"
        />
      </div>
      <div>
        {report?.reportTemplate?.organizationLogoAllowed && (
          <>
            <div>
              <Paragraph displayType="loud" colorType="primary">
                Organization Logo
              </Paragraph>
              <div className={`mt-2 ${!report?.organization?.templateLogoSignedUrl && !reportIsCompleted && "-mb-6"} `}>
                <UploadLogo
                  S3BucketUrlRetriever={(params) =>
                    S3BucketUrlRetriever({
                      ...params,
                    })
                  }
                  S3BucketUploadCallback={(params) =>
                    S3BucketUploadCallback({
                      ...params,
                    })
                  }
                  reportFileUploadCompleteCallback={() => Promise.resolve()}
                  fileParseStatusRetriever={(fileId) =>
                    new Promise((resolve) => {
                      setTimeout(() => resolve({ fileId, status: "done" }), 1000);
                    })
                  }
                  removeFileCallback={() => Promise.resolve()}
                  logoSignedUrl={report?.organization?.templateLogoSignedUrl ?? ""}
                  onSave={onSave}
                  width="w-100"
                  displayOnlyMode={reportIsCompleted}
                />
              </div>
              {!reportIsCompleted && <Small>Recommended size: 200 x 80px</Small>}
            </div>
            <Divider></Divider>
          </>
        )}

        <div className="mt-4">
          <Paragraph displayType="loud" colorType="primary">
            Header Text
          </Paragraph>
          <TextArea
            value={headerContent}
            placeholder="Enter Header Text"
            displayOnlyMode={reportIsCompleted}
            onChange={(e) => {
              setHeaderContent(e.currentTarget.value);
            }}
            label=""
          />
        </div>
      </div>
      {!reportIsCompleted && (
        <div className="flex space-x-2 pt-6 justify-end">
          <Button
            appearance="secondary"
            className="px-6"
            text="Cancel"
            onClick={() => {
              setHeaderContent(report?.headerPageHeading ?? "");
              ref?.current?.click();
            }}
          />
          <Button
            appearance="primary"
            text="Save Settings"
            className="px-6"
            onClick={() => {
              handleOnSave(headerContent);
              ref?.current?.click();
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <PopoverPanel direction="bottom-right" width="w-[500px]" height="max-h-[610px]" content={popoverContent}>
      <Button
        text={
          <div ref={ref} className="flex items-center text-green-700">
            <Cog6ToothIcon className="mr-1 h-4 w-4" />
            <Small colorType="primary" className="text-green-700">
              Report Settings
            </Small>
          </div>
        }
        appearance="secondary"
        className="right ring-green-400"
      />
    </PopoverPanel>
  );
};
